import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Fab, CircularProgress } from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import CreateApi from "../../Settings/appSettings";
import { useDataContext } from "../../Context/useDataContext";
import { useLSContext } from "../../Context/useLSContext";
import { fetchLeaderboardData } from "../Functions/fetchLeaderboardData";
import { toIdString } from "../../utils/courseDataHelpers";
import { EventSelector } from "../Components/EventSelector";
import { LeaderboardDisplay } from "@moment-golf/leaderboard";

interface LeaderboardProps {
    availableEvents: any;
}

const EPOCH_REF_DATE = new Date("1/1/2000");

interface FetchOptions {
    refDateOverride?: Date;
    currentDataOverride?: any[];
}

export const Leaderboard = ({ availableEvents }: LeaderboardProps) => {
    const api = CreateApi();

    const {
        isHeadless,
        hideSelector,
        factor,
        appTheme,
        windowSize,
        isPWA,
        setToggleScoring,
    } = useLSContext();

    const {
        eventData,
        leaderboardData,
        currentRound,
        setCurrentRound,
        setLeaderboardData,
        leaderboardLastQuery,
        setLeaderboardLastQuery,
        leaderboardCourseData,
        setLeaderboardCourseData,
    } = useDataContext();

    const [leaderboardRefreshing, setLeaderboardRefreshing] = useState(false);
    const [eventInfo, setEventInfo] = useState<any>(null);
    const [seriesPoints, setSeriesPoints] = useState<any>(undefined);
    const appBarHeightPx = windowSize.appBarHeight ?? 60;

    const fetchGenerationRef = useRef(0);

    const eventId = toIdString(eventData?._id);
    const displayEventData = eventInfo ? { ...eventData, ...eventInfo } : eventData;

    // Tell LSContext the leaderboard tab is active
    useEffect(() => {
        const id = setTimeout(() => setToggleScoring(true), 100);
        return () => clearTimeout(id);
    }, [setToggleScoring]);

    const handleFetchLeaderboardData = useCallback(
        async (options?: FetchOptions) => {
            const eventID = toIdString(eventData?._id);
            if (!eventID) return;

            const generation = ++fetchGenerationRef.current;
            const refDate = options?.refDateOverride ?? leaderboardLastQuery;
            const currentLeaderboardData =
                options?.currentDataOverride ?? leaderboardData;

            const result = await fetchLeaderboardData({
                api,
                eventID,
                refDate,
                currentLeaderboardData,
            });

            if (generation !== fetchGenerationRef.current) return;

            if (result.eventInfo) {
                setEventInfo(result.eventInfo);
            }
            setLeaderboardLastQuery(new Date());
            setLeaderboardData(result.updatedLeaderboardData);
            setLeaderboardCourseData(result.leaderBoardCourseData ?? []);
        },
        [
            api,
            eventData?._id,
            leaderboardLastQuery,
            leaderboardData,
            setLeaderboardData,
            setLeaderboardLastQuery,
            setLeaderboardCourseData,
        ]
    );

    const fetchHandlerRef = useRef(handleFetchLeaderboardData);
    fetchHandlerRef.current = handleFetchLeaderboardData;

    // Reset and full-fetch on event change
    useEffect(() => {
        if (!eventId) return;

        fetchGenerationRef.current += 1;
        const generation = fetchGenerationRef.current;

        setCurrentRound(1);
        setLeaderboardData([]);
        setLeaderboardLastQuery(EPOCH_REF_DATE);
        setEventInfo(null);
        setLeaderboardCourseData([]);
        setSeriesPoints(undefined);

        (async () => {
            const result = await fetchLeaderboardData({
                api,
                eventID: eventId,
                refDate: EPOCH_REF_DATE,
                currentLeaderboardData: [],
            });

            if (generation !== fetchGenerationRef.current) return;

            if (result.eventInfo) {
                setEventInfo(result.eventInfo);
            }
            setLeaderboardLastQuery(new Date());
            setLeaderboardData(result.updatedLeaderboardData);
            setLeaderboardCourseData(result.leaderBoardCourseData ?? []);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    // Date-bounded polling — delta fetch every 60 s while the event is active
    const pollStartDate = displayEventData?.startDate;
    const pollEndDate = displayEventData?.endDate;

    useEffect(() => {
        if (!pollStartDate || !pollEndDate) return;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const start = new Date(pollStartDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(pollEndDate);
        end.setHours(0, 0, 0, 0);

        if (today >= start && today <= end) {
            const interval = setInterval(() => fetchHandlerRef.current(), 60000);
            return () => clearInterval(interval);
        }
    }, [eventId, pollStartDate, pollEndDate]);

    // Series points when eventGroupIDs are available (from eventInfo after fetch)
    const seriesGroupId = toIdString(
        (eventInfo?.eventGroupIDs ?? eventData?.eventGroupIDs ?? [])[0]
    );

    useEffect(() => {
        if (!seriesGroupId) {
            setSeriesPoints(undefined);
            return;
        }
        api.get("/lb/GetGroupSeriesPoints", { params: { _id: seriesGroupId } })
            .then((res: any) => setSeriesPoints(res.data?.seriesPoints ?? undefined))
            .catch(() => setSeriesPoints(undefined));
    }, [api, seriesGroupId]);

    const showSelector =
        !hideSelector && ((windowSize.isMobile || windowSize.verticalMobile) || isHeadless);

    const headerSlot = showSelector ? (
        <Box sx={{
            width: '100%',
            maxWidth: 600,
            mx: 'auto',
            px: 1,
            pt: 1,
            pb: 0.5,
            boxSizing: 'border-box',
            flexShrink: 0,
            mt: `${appBarHeightPx}px`,
        }}>
            <EventSelector
                availableEvents={availableEvents}
                eventData={eventData}
                variant="outlined"
                color="primary.main"
                showStartAdornment={false}
                sx={{ padding: 0, width: '100%', flex: 'none', minWidth: 0 }}
            />
        </Box>
    ) : undefined;

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                position: "relative",
                zIndex: 0,
                overscrollBehavior: "none",
                backgroundColor: "sepia.background",
            }}
            className={isHeadless ? "leaderboard-embed" : "live-scoring"}
        >
            <LeaderboardDisplay
                leaderboardData={leaderboardData}
                courseData={leaderboardCourseData}
                eventData={displayEventData}
                currentRound={currentRound}
                availableEvents={availableEvents}
                factor={factor}
                appTheme={appTheme}
                windowSize={windowSize}
                isPWA={isPWA}
                headless={isHeadless}
                embedded={isHeadless}
                hideSelector={hideSelector}
                headerSlot={headerSlot}
                seriesPoints={seriesPoints}
            />
            <Fab
                size="small"
                color="primary"
                aria-label="Refresh leaderboard"
                disabled={leaderboardRefreshing || !eventData?._id}
                onClick={async () => {
                    if (!eventData?._id || leaderboardRefreshing) return;
                    setLeaderboardRefreshing(true);
                    try {
                        await handleFetchLeaderboardData();
                    } finally {
                        setLeaderboardRefreshing(false);
                    }
                }}
                sx={{
                    position: "fixed",
                    top: `calc(${appBarHeightPx}px + 8px)`,
                    right: `max(12px, env(safe-area-inset-right, 0px))`,
                    zIndex: 150,
                }}
            >
                {leaderboardRefreshing ? (
                    <CircularProgress color="inherit" size={22} />
                ) : (
                    <Refresh />
                )}
            </Fab>
        </Box>
    );
};
